import { Checkbox, Dropdown, DropdownItem } from '@helix-ds/react'
import { ChevronDown, Filter } from 'flowbite-react-icons/outline'
import type { WorkspaceStatusOption } from './useWorkspaceFilters'

type Props = {
  options: WorkspaceStatusOption[]
  /** Selected DISPLAYED labels (multi-select). */
  selected: ReadonlySet<string>
  /** True when every present status is selected (the default state) → shown as "All statuses". */
  allSelected: boolean
  onToggle: (label: string) => void
  onClear: () => void
  /** Select every present status (== Clear / all-selected default). */
  onSelectAll: () => void
  /** Deselect every present status (deliberate "show nothing"). */
  onDeselectAll: () => void
}

/**
 * Label-based multi-status filter dropdown (design §8). Options are keyed by the DISPLAYED
 * clinician label, so raw statuses collapse into one option ("Unknown" for the blank/unmapped
 * bucket). Multi-select stays open (`dismissOnClick={false}`). All aria-labels interpolate ONLY
 * status labels + fixed strings — never patient name/MRN/DOB (PHI invariant §9a).
 */
export function WorkspaceStatusFilter({
  options,
  selected,
  allSelected,
  onToggle,
  onClear,
  onSelectAll,
  onDeselectAll,
}: Props) {
  const activeCount = selected.size
  // Trigger text must never lie: "All statuses" ONLY when everything is selected (allSelected). An
  // empty selection means the opposite (showing nothing) and reads "No statuses" — NOT "All".
  const triggerLabel = allSelected ? 'All statuses' : activeCount === 0 ? 'No statuses' : `${activeCount} selected`
  // Show chips + Clear for ANY non-default state, INCLUDING the empty selection. Clear must stay
  // reachable when nothing is selected, otherwise the user is trapped with an empty list and no way
  // back to "All statuses".
  const showControls = !allSelected
  // The header bulk-action flips based on current state: if everything is on, offer "Deselect all";
  // otherwise offer "Select all". Disabled when there are no options to act on.
  const hasOptions = options.length > 0

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Dropdown
        dismissOnClick={false}
        renderTrigger={() => (
          <button
            type="button"
            aria-label="Filter orders by status"
            aria-haspopup="menu"
            className="inline-flex items-center gap-1.5 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-600"
          >
            <Filter className="h-4 w-4" aria-hidden />
            <span>{triggerLabel}</span>
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border-base dark:border-gray-700 px-3 py-2">
          <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">Status</span>
          <button
            type="button"
            onClick={allSelected ? onDeselectAll : onSelectAll}
            disabled={!hasOptions}
            aria-label={allSelected ? 'Deselect all statuses' : 'Select all statuses'}
            className="rounded-full px-2 py-0.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:cursor-not-allowed disabled:text-text-body-subtle dark:disabled:text-gray-500 disabled:no-underline"
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        {options.map((opt) => {
          const checked = selected.has(opt.label)
          return (
            <DropdownItem
              key={opt.label}
              onClick={() => onToggle(opt.label)}
              role="menuitemcheckbox"
              aria-checked={checked}
            >
              <span className="flex w-full items-center gap-2 text-sm text-text-body dark:text-gray-400">
                <Checkbox checked={checked} readOnly aria-hidden tabIndex={-1} className="pointer-events-none" />
                <span className="flex-1">{opt.label}</span>
              </span>
            </DropdownItem>
          )
        })}
      </Dropdown>

      {showControls && (
        <>
          {[...selected].map((label) => (
            <button
              key={label}
              type="button"
              onClick={() => onToggle(label)}
              aria-label={`Remove ${label} status filter`}
              className="inline-flex items-center gap-1 rounded-full bg-background-secondary-medium dark:bg-gray-800 px-2.5 py-1 text-xs font-medium text-text-body dark:text-gray-400 hover:bg-background-tertiary dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-600"
            >
              {label} <span aria-hidden>x</span>
            </button>
          ))}
          <button
            type="button"
            onClick={onClear}
            className="rounded-full px-2.5 py-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-600"
          >
            Clear
          </button>
        </>
      )}
    </div>
  )
}
